import React, { createContext, useContext, useEffect, useState } from 'react';
import { BIGTIME_WHATSAPP } from '@/mock/products';
import { useSelectedStore, useOrders } from '@/store/dataStore';

const CartContext = createContext(null);
export const useCart = () => useContext(CartContext);

const STORAGE_KEY = 'bigtime_cart_v1';
const WHOLESALE_THRESHOLD_USD = 300;   // mirrors backend WHOLESALE_THRESHOLD_USD
const WA_MAX_URL = 3000;   // wa.me / browsers reject very long ?text= links

// Full detail when it fits; otherwise one line per item; otherwise as many as fit + "y N más".
function buildWaUrl(target, head, fullItems, compactItems, foot, code) {
  const make = (body) => `https://api.whatsapp.com/send?phone=${target}&text=${encodeURIComponent([...head, ...body, ...foot].join('\n'))}`;
  let url = make(fullItems);
  if (url.length <= WA_MAX_URL) return url;
  url = make([...compactItems, '']);
  if (url.length <= WA_MAX_URL) return url;
  for (let n = compactItems.length - 1; n >= 0; n -= 1) {
    const rest = compactItems.length - n;
    url = make([...compactItems.slice(0, n), `…y ${rest} artículo(s) más. Pedido completo: #${code} en el panel.`, '']);
    if (url.length <= WA_MAX_URL || n === 0) return url;
  }
  return url;
}


export function CartProvider({ children }) {
  const { store } = useSelectedStore();
  const { addOrder } = useOrders();
  const [items, setItems] = useState(() => {
    try { const raw = localStorage.getItem(STORAGE_KEY); return raw ? JSON.parse(raw) : []; } catch { return []; }
  });
  const [isOpen, setIsOpen] = useState(false);
  const [giftCard, setGiftCard] = useState(null); // {code, percent}

  useEffect(() => { localStorage.setItem(STORAGE_KEY, JSON.stringify(items)); }, [items]);

  const addItem = (product, qty = 1, opts = {}) => {
    setItems((prev) => {
      const color = opts.color || product.color || null;
      const size = opts.size || product.size || null;
      // Unique cart key includes variant so different combos are separate lines.
      const key = `${product.id}::${(color?.name || color || '')}::${size || ''}`;
      const existing = prev.find((i) => (i.__key || i.id) === key);
      if (existing) return prev.map((i) => (i.__key || i.id) === key ? { ...i, qty: i.qty + qty } : i);
      return [...prev, {
        __key: key,
        id: product.id, name: product.name, price: product.price, image: (color?.image || product.image), qty,
        // Preserve BOTH tiers so the cart can switch dynamically when total crosses $300.
        retail_price: product.price,
        wholesale_price: product.wholesale_price ?? null,
        color, size,
        internal_code: product.internal_code || '', barcode: product.barcode || '',
      }];
    });
    setIsOpen(true);
  };
  const updateQty = (id, qty) => { if (qty <= 0) return removeItem(id); setItems((prev) => prev.map((i) => (i.__key || i.id) === id ? { ...i, qty } : i)); };
  const removeItem = (id) => setItems((prev) => prev.filter((i) => (i.__key || i.id) !== id));
  const clearCart = () => { setItems([]); setGiftCard(null); };

  // ------- Two-tier pricing -------
  // The "al mayor" subtotal is what determines whether the wholesale tier
  // kicks in. Once it does, EVERY line uses its `wholesale_price` (or falls
  // back to `retail_price` if the product doesn't have one loaded).
  const retailSubtotal = items.reduce((s, i) => s + (i.retail_price ?? i.price) * i.qty, 0);
  const wholesaleActive = retailSubtotal >= WHOLESALE_THRESHOLD_USD;
  const effectivePrice = (i) => (wholesaleActive && i.wholesale_price != null ? i.wholesale_price : (i.retail_price ?? i.price));
  const rawSubtotal = items.reduce((s, i) => s + effectivePrice(i) * i.qty, 0);
  const discount = giftCard ? Math.round(rawSubtotal * giftCard.percent) / 100 : 0;
  const subtotal = Math.max(0, Math.round((rawSubtotal - discount) * 100) / 100);
  const count = items.reduce((sum, i) => sum + i.qty, 0);
  // Savings shown in UI when the wholesale tier is active.
  const wholesaleSavings = wholesaleActive
    ? Math.max(0, Math.round((retailSubtotal - rawSubtotal) * 100) / 100)
    : 0;
  const amountToReachWholesale = wholesaleActive ? 0 : Math.max(0, Math.round((WHOLESALE_THRESHOLD_USD - retailSubtotal) * 100) / 100);

  const checkoutWhatsApp = async (customer, opts = {}) => {
    if (items.length === 0) return null;
    // Open the tab synchronously (user gesture) so popup blockers don't kill it on slow, large orders.
    const waWin = window.open('', '_blank');
    let order;
    try {
    // Create order on server FIRST. Never open WhatsApp if the sale wasn't persisted.
    order = await addOrder({
      storeId: store?.id || null,
      customer,
      items: items.map((i) => ({
        id: i.id, name: i.name, price: effectivePrice(i), qty: i.qty,
        color: i.color?.name || null, size: i.size || null,
        internal_code: i.internal_code || '', barcode: i.barcode || '',
      })),
      subtotal: rawSubtotal,  // send RAW subtotal; backend re-prices and applies gift card
      salesType: opts.salesType || 'online',
      giftCardCode: giftCard?.code || null,
      payments: opts.payments || null,
    });
    } catch (err) {
      if (waWin) waWin.close();
      throw err;
    }

    const now = new Date();
    const fecha = now.toLocaleDateString('es-VE', { day: '2-digit', month: '2-digit', year: 'numeric' });
    const hora = now.toLocaleTimeString('es-VE', { hour: '2-digit', minute: '2-digit' });
    // Server is the source of truth for pricing (re-priced + gift card applied)
    const serverSubtotal = typeof order?.subtotal === 'number' ? order.subtotal : subtotal;
    const serverGift = order?.gift_card_applied;
    const serverDiscount = serverGift?.discount_usd || discount;
    const preDiscountTotal = serverGift ? Math.round((serverSubtotal + serverDiscount) * 100) / 100 : rawSubtotal;

    const lines = [];
    lines.push('🛍️ *NUEVO PEDIDO — BIGTIME*');
    lines.push('_Todo en un solo lugar._');
    if (store) lines.push(`🏬 *Tienda:* ${store.name}`);
    lines.push('━━━━━━━━━━━━━━━━━━');
    lines.push(`📄 *Orden:* #${order.code}`);
    lines.push(`📅 *Fecha:* ${fecha} — ${hora}`);
    lines.push(`👤 *Cliente:* ${customer.name}`);
    lines.push(`🪪 *Cédula:* ${customer.cedula}`);
    lines.push(`📱 *Teléfono:* ${customer.phone}`);
    lines.push(`📍 *Ciudad:* ${customer.city}${customer.state ? ', ' + customer.state : ''}`);
    if (customer.address) lines.push(`🏠 *Dirección:* ${customer.address}`);
    if (customer.note) lines.push(`📝 *Nota:* ${customer.note}`);
    if (customer.purchaseType) {
      const ptLabels = { emprendedor: '💼 Emprendedor', regalo: '🎁 Regalo', personal: '👤 Uso Personal' };
      lines.push(`🏷️ *Tipo de compra:* ${ptLabels[customer.purchaseType] || customer.purchaseType}`);
    }
    lines.push('━━━━━━━━━━━━━━━━━━');
    lines.push('🧾 *Detalle del pedido:*');
    lines.push('');
    const head = lines.splice(0);
    const fullItems = [];
    const compactItems = [];
    items.forEach((i, idx) => {
      const unitPrice = effectivePrice(i);
      const extra = [i.color?.name, i.size, i.internal_code && `CI:${i.internal_code}`].filter(Boolean).join(' · ');
      compactItems.push(`${idx + 1}. ${i.name}${extra ? ` (${extra})` : ''} x${i.qty} = $${(unitPrice * i.qty).toFixed(2)}`);
      lines.push(`*${idx + 1}. ${i.name}*`);
      // Variant labels prevent the classic "which color did you want?" confusion.
      const variantBits = [];
      if (i.color?.name) variantBits.push(`Color: ${i.color.name}`);
      if (i.size)        variantBits.push(`Talla: ${i.size}`);
      if (variantBits.length) lines.push(`   • Variante: ${variantBits.join(' / ')}`);
      // Codes so the warehouse can locate the SKU without opening the app.
      const codeBits = [];
      if (i.internal_code) codeBits.push(`CI: ${i.internal_code}`);
      if (i.barcode)       codeBits.push(`CB: ${i.barcode}`);
      if (codeBits.length) lines.push(`   • Códigos: ${codeBits.join('  |  ')}`);
      lines.push(`   • Cantidad: ${i.qty}`);
      lines.push(`   • Precio unit.: $${unitPrice.toFixed(2)}${wholesaleActive && i.wholesale_price != null ? '  🎁 Mayorista' : ''}`);
      lines.push(`   • Subtotal: *$${(unitPrice * i.qty).toFixed(2)}*`);
      if (i.image && i.image.startsWith('http')) lines.push(`   • 🖼️ ${i.image}`);
      lines.push('');
      fullItems.push(lines.splice(0).join('\n'));
    });
    lines.push('━━━━━━━━━━━━━━━━━━');
    const totalItems = items.reduce((s, i) => s + i.qty, 0);
    lines.push(`📦 *Total artículos:* ${totalItems}`);
    if (wholesaleActive && wholesaleSavings > 0) {
      lines.push(`🎁 *Descuento mayorista aplicado:* -$${wholesaleSavings.toFixed(2)} (compra ≥ $${WHOLESALE_THRESHOLD_USD})`);
    }
    if (serverGift && serverDiscount > 0) {
      lines.push(`💵 *Subtotal:* $${preDiscountTotal.toFixed(2)}`);
      lines.push(`🎁 *Gift Card ${serverGift.code} (${serverGift.percent}% OFF):* -$${serverDiscount.toFixed(2)}`);
    }
    lines.push(`💰 *TOTAL A PAGAR: $${serverSubtotal.toFixed(2)}*`);
    // Payment breakdown (multi-tender). Optional; only shown when the seller
    // registered payments in the checkout form.
    const pays = opts.payments || [];
    if (pays.length > 0) {
      lines.push('');
      lines.push('💳 *Pagos registrados:*');
      const METHOD_LABELS = {
        efectivo_usd: 'Efectivo $', efectivo_eur: 'Efectivo €', cop: 'Efectivo COP',
        bs: 'Efectivo Bs', usdt: 'USDT', zelle: 'Zelle', pagomovil: 'Pago Móvil', binance: 'Binance Pay',
      };
      pays.forEach((p, i) => {
        const lbl = METHOD_LABELS[p.method] || p.method;
        let line = `   ${i + 1}. ${lbl}: ${p.amount.toFixed(2)} ${p.currency}`;
        if (p.reference) line += `  |  Ref: ${p.reference}`;
        lines.push(line);
      });
    }
    lines.push(`📊 *Estado:* Pendiente`);
    lines.push('━━━━━━━━━━━━━━━━━━');
    lines.push('');
    lines.push('¡Gracias por elegir *BIGTIME*! 💛');
    lines.push('Por favor confirme disponibilidad y método de pago.');

    const target = store?.whatsapp || BIGTIME_WHATSAPP;
    const url = buildWaUrl(target, head, fullItems, compactItems, lines, order.code);
    if (waWin) waWin.location.href = url; else window.location.href = url;
    // Do NOT clearCart here — CartDrawer clears after user confirms notify flow
    return order;
  };

  return (
    <CartContext.Provider value={{
      items, isOpen, setIsOpen, addItem, updateQty, removeItem, clearCart,
      subtotal, rawSubtotal, discount, giftCard, setGiftCard, count, checkoutWhatsApp,
      // Two-tier pricing state, so the UI can show the wholesale banner + savings.
      wholesaleActive, wholesaleSavings, retailSubtotal, amountToReachWholesale,
      wholesaleThreshold: WHOLESALE_THRESHOLD_USD, effectivePrice,
    }}>
      {children}
    </CartContext.Provider>
  );
}
